import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiArrowLeft, FiCalendar, FiEdit2, FiLoader } from 'react-icons/fi';
import { getStatusColor } from '../data/helpers';
import CampaignProgressTable from '../components/campaigns/CampaignProgressTable';
import CampaignOverviewPanel from '../components/campaigns/CampaignOverviewPanel';
import CreateCampaignModal from '../components/campaigns/CreateCampaignModal';
import { dataFetch } from '../api';

const typeColor = {
  Mandatory:  'bg-slate-50 text-slate-600 border-slate-200',
  Remedial:   'bg-slate-50 text-slate-600 border-slate-200',
  Onboarding: 'bg-slate-50 text-slate-600 border-slate-200',
  Compliance: 'bg-slate-50 text-slate-600 border-slate-200',
};

const getArray = (val) => {
  if (Array.isArray(val)) return val;
  if (!val) return [];
  if (typeof val === 'string') {
    try {
      const parsed = JSON.parse(val);
      return Array.isArray(parsed) ? parsed : [parsed];
    } catch {
      if (val.includes(',')) return val.split(',').map(s => s.trim());
      return [val];
    }
  }
  return [val];
};

const getLearnerId = (learner) => {
  if (learner && typeof learner === 'object') {
    return learner.id ?? learner.employee_id ?? '';
  }
  return learner ?? '';
};

const CampaignDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [campaign, setCampaign] = useState(null);
  const [modulesList, setModulesList] = useState([]);
  const [employeesList, setEmployeesList] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showEdit, setShowEdit] = useState(false);

  const fetchCampaign = async () => {
    try {
      const res = await dataFetch('read/campaigns');
      if (res?.status === 'success' && res.data) {
        const found = res.data.find(c => String(c.id) === String(id));
        if (found) {
          setCampaign({
            ...found,
            assignedLearners: getArray(found.assignedLearners),
            trainingContent: getArray(found.trainingContent)
          });
        } else {
          setError('Campaign not found');
        }
      } else {
        setError('Failed to fetch campaign details');
      }
    } catch (err) {
      console.error(err);
      setError('Failed to fetch campaign details');
    }
  };

  const fetchModules = async () => {
    try {
      const res = await dataFetch('read/modules');
      if (res?.status === 'success') {
        setModulesList(res.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch modules', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await dataFetch('read/employees?limit=500');
      if (res?.status === 'success') {
        setEmployeesList(res.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch employees', error);
    }
  };

  const fetchAssignments = async () => {
    try {
      const res = await dataFetch(`read/assignments?campaign_id=${id}&limit=5000`);
      if (res?.status === 'success') {
        setAssignments(res.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch assignments', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchCampaign(), fetchModules(), fetchEmployees(), fetchAssignments()]);
      setLoading(false);
    };
    loadData();
  }, [id]);

  if (loading) return (
    <div className="flex flex-col items-center justify-center h-64 text-slate-500">
      <SafeIcon icon={FiLoader} className="animate-spin text-3xl mb-4" />
      <p className="text-sm font-medium">Loading campaign details...</p>
    </div>
  );

  if (error || !campaign) return (
    <div className="flex flex-col items-center justify-center h-64 text-slate-500">
      <p className="text-lg font-semibold">{error || 'Campaign not found'}</p>
      <button onClick={() => navigate('/campaigns')} className="mt-4 text-blue-600 hover:underline text-sm font-medium">← Back to Campaigns</button>
    </div>
  );

  const modules = (campaign.trainingContent || []).map(mid => modulesList.find(m => String(m.id) === String(mid))).filter(Boolean);
  
  const assignedLearnerIds = new Set([
    ...(campaign.assignedLearners || []).map(getLearnerId).filter(Boolean).map(String),
    ...(assignments || []).map(a => a?.employee_id ?? '').filter(Boolean).map(String)
  ]);

  const assignedLearners = employeesList.filter(e => assignedLearnerIds.has(String(e?.id)));

  const completedLearners = assignedLearners.filter(l => {
    const learnerAssignments = assignments.filter(a => String(a.employee_id) === String(l.id));
    return learnerAssignments.length > 0 && learnerAssignments.every(a => a.status === 'Completed');
  }).length;

  const calculatedCompletionRate = assignedLearners.length > 0 
    ? Math.round((completedLearners / assignedLearners.length) * 100)
    : 0;

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      <div className="flex items-center justify-between">
        <button onClick={() => navigate('/campaigns')} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 transition-colors font-medium">
          <SafeIcon icon={FiArrowLeft} /> Back to Campaigns
        </button>
        <button onClick={() => setShowEdit(true)} className="flex items-center gap-2 text-sm text-blue-600 bg-blue-50 hover:bg-blue-100 px-4 py-2 rounded-lg font-medium transition-colors">
          <SafeIcon icon={FiEdit2} className="text-xs" /> Edit Campaign
        </button>
      </div>

      {/* Header */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="h-16 bg-slate-800" />
        <div className="px-6 pb-6 pt-4">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
            <div>
              <div className="flex gap-2 flex-wrap mb-2">
                <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${typeColor[campaign.campaignType] || typeColor.Mandatory}`}>{campaign.campaignType}</span>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(campaign.status)}`}>{campaign.status}</span>
              </div>
              <h2 className="text-xl font-bold text-slate-800">{campaign.campaignName}</h2>
              <p className="text-sm text-slate-500 mt-1 flex items-center gap-1.5">
                <SafeIcon icon={FiCalendar} /> {campaign.startDate} → {campaign.endDate}
              </p>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-3xl font-bold text-slate-900">{calculatedCompletionRate}%</p>
              <p className="text-xs text-slate-500">completion rate</p>
            </div>
          </div>
          
          <div className="w-full bg-slate-100 rounded-full h-2.5">
            <div className={`h-2.5 rounded-full ${calculatedCompletionRate === 100 ? 'bg-emerald-500' : calculatedCompletionRate >= 60 ? 'bg-blue-500' : 'bg-amber-400'}`} style={{ width: `${calculatedCompletionRate}%` }} />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 space-y-6">
          <CampaignProgressTable campaign={campaign} assignedLearners={assignedLearners} assignments={assignments} />
        </div>
        <div className="space-y-6">
          <CampaignOverviewPanel campaign={campaign} modules={modules} assignedLearners={assignedLearners} assignments={assignments} />
        </div>
      </div>

      {showEdit && (
        <CreateCampaignModal 
          initialData={campaign}
          onClose={() => setShowEdit(false)}
          onUpdate={(updatedCampaign) => {
            setCampaign(updatedCampaign);
            setShowEdit(false);
          }} 
        />
      )}
    </motion.div>
  );
};

export default CampaignDetail;